/**
 * Build the link a customer shares from Refer & Earn.
 *
 * The friend opening it has none of the sharer's context — no localStorage,
 * often not even the same host shape — so the link must pin the tenant by
 * itself. Native apps and shared hosts serve every tenant from one origin
 * (`https://dairyfresh.localappstore.in/t/<slug>/…`, rewritten to
 * `/app/…?t=<slug>` by TenantRedirect), so a bare `${origin}/login?ref=CODE`
 * resolved to whichever tenant owns that host (or the platform default) and
 * the code was silently dropped on the wrong store.
 *
 * `?t=<slug>` is the FIRST thing lib/tenant.js#getTenantSlug consults, and
 * `/login` is the customer login route on every host, so
 * `${origin}/login?t=<slug>&ref=<CODE>` lands on the right store whether the
 * app is on a custom domain, a tenant subdomain, a `/t/<slug>` path or inside
 * a native WebView. On a host that already implies the tenant the extra
 * parameter is simply redundant.
 *
 * Returns "" when there is no code, or when the page is not served over
 * http(s) (e.g. a bundled `capacitor://` / `file://` shell) — a link to that
 * origin would be useless to the friend, so the caller shares text only.
 *
 * Pure: pass `origin` (window.location.origin) and the tenant slug in.
 */
export function buildReferralShareUrl({ origin, slug, code } = {}) {
  const ref = String(code || "").trim().toUpperCase();
  if (!ref) return "";
  const base = String(origin || "").trim().replace(/\/+$/, "");
  if (!/^https?:\/\/[^/]+$/i.test(base)) return "";
  const params = new URLSearchParams();
  const tenant = String(slug || "").trim();
  if (tenant) params.set("t", tenant);
  params.set("ref", ref);
  return `${base}/login?${params.toString()}`;
}
